import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  getSkillList,
  getSkillsByCategoryId,
  createSkill,
  updateSkill,
  deleteSkill,
} from "../api/skill.api";
import type { SkillPayload, GetSkillListParams } from "@/interface/masterManagement";
import { skillKeys, STALE_5_MIN } from "@/lib/queryKey";
import { getNextPage, isPaginated } from "@/utils/helper";

export const useSkillList = (params: GetSkillListParams) =>
  useQuery({
    queryKey: skillKeys.list(params),
    queryFn: () => getSkillList(params),
    ...STALE_5_MIN,
  });

export const useInfiniteSkillList = (params?: GetSkillListParams) => {
  const hasPagination = isPaginated(params);

  return useInfiniteQuery({
    queryKey: skillKeys.list({ ...params, type: "infinite" }),
    queryFn: ({ pageParam }) =>
      getSkillList(
        hasPagination ? { ...params, page: pageParam } : params,
      ),
    initialPageParam: params?.page ?? 1,
    getNextPageParam: (lastPage, allPages) =>
      hasPagination
        ? getNextPage(lastPage, allPages, params?.page_size)
        : undefined,
    ...STALE_5_MIN,
  });
};

export const useSkillsByCategoryId = (categoryId?: number | string) =>
  useQuery({
    queryKey: skillKeys.detail(categoryId ?? ""),
    queryFn: () => getSkillsByCategoryId(categoryId!),
    enabled: Boolean(categoryId),
    ...STALE_5_MIN,
  });

export const useInfiniteSkillsByCategoryId = (
  categoryId?: number | string,
  params?: GetSkillListParams,
) => {
  const hasPagination = isPaginated(params);

  return useInfiniteQuery({
    queryKey: skillKeys.list({ categoryId, type: "infinite", ...params }),
    queryFn: ({ pageParam }) =>
      getSkillsByCategoryId(
        categoryId!,
        hasPagination ? { ...params, page: pageParam } : params,
      ),
    initialPageParam: params?.page ?? 1,
    getNextPageParam: (lastPage, allPages) =>
      hasPagination
        ? getNextPage(lastPage, allPages, params?.page_size)
        : undefined,
    enabled: Boolean(categoryId),
    ...STALE_5_MIN,
  });
};

export const useCreateSkill = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSkill,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: skillKeys.lists() });
    },
  });
};

export const useUpdateSkill = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number | string; payload: SkillPayload }) =>
      updateSkill(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: skillKeys.lists() });
    },
  });
};

export const useDeleteSkill = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number | string) => deleteSkill(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: skillKeys.lists() });
    },
  });
};
